import React from 'react';
import { Users, VideoCamera, CalendarBlank } from '@phosphor-icons/react';

// Analytics API row shape from learn_my_upcoming_sessions metric
interface SessionRow {
    id: string | number;
    eventName: string;
    eventMode?: string;
    eventStartDate?: string;
    eventStartTime?: string;
    eventEndTime?: string;
    attendanceStatus: string;
    trainerName?: string;
    externalTrainer?: string;
    externalLocation?: string;
}

interface MyUpcomingSessionsWidgetProps {
    data?: SessionRow[] | null;
    loading?: boolean;
    error?: string;
}

const formatSessionTime = (startDate?: string, startTime?: string, endTime?: string): string => {
    if (!startDate) return '';
    const dateStr = new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (!startTime) return dateStr;
    return endTime ? `${dateStr}, ${startTime} - ${endTime}` : `${dateStr}, ${startTime}`;
};

const getStatusBadge = (status: string) => {
    const s = status?.toLowerCase();
    if (s === 'attended') return { label: 'Confirmed', className: 'bg-emerald-100 text-emerald-700' };
    if (s === 'enrolled' || s === 'registered') return { label: 'Registered', className: 'bg-blue-100 text-blue-700' };
    return { label: status || 'Registered', className: 'bg-blue-100 text-blue-700' };
};

export const MyUpcomingSessionsWidget: React.FC<MyUpcomingSessionsWidgetProps> = ({ data, loading, error }) => {
    const sessions = Array.isArray(data) ? data : [];

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 pt-4 pb-4 flex items-center justify-between">
                <span className="text-xs text-gray-400">{sessions.length} upcoming</span>
            </div>
            <div className="flex-1 overflow-auto px-4 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="w-6 h-6 border-3 border-gray-200 border-t-primary rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="flex items-center justify-center h-full text-red-500 text-sm">{error}</div>
                ) : sessions.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <CalendarBlank className="w-8 h-8" />
                        <span className="text-sm">No upcoming sessions</span>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {sessions.map((session, idx) => {
                            const badge = getStatusBadge(session.attendanceStatus);
                            const isFirst = idx === 0;
                            const isVirtual = session.eventMode?.toLowerCase() === 'virtual' || session.eventMode?.toLowerCase() === 'online';
                            const Icon = isVirtual ? VideoCamera : Users;
                            const iconBg = isVirtual ? 'bg-blue-100' : 'bg-purple-100';
                            const iconColor = isVirtual ? 'text-blue-600' : 'text-purple-600';
                            const timeColor = isFirst ? 'text-purple-600 font-medium' : 'text-gray-500';
                            const trainerName = session.trainerName || session.externalTrainer;
                            const venue = session.externalLocation;

                            return (
                                <div
                                    key={session.id}
                                    className={`flex items-center gap-3 p-3 rounded-lg ${
                                        isFirst ? 'bg-purple-50/50 border border-purple-100' : 'hover:bg-gray-50'
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
                                        <Icon className={`w-5 h-5 ${iconColor}`} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium text-gray-900 truncate">{session.eventName}</div>
                                        <div className="text-[11px] text-gray-500 mt-0.5 truncate">
                                            {trainerName && `Trainer: ${trainerName}`}
                                            {trainerName && venue && ' · '}
                                            {venue}
                                        </div>
                                        <div className={`text-[11px] mt-1 ${timeColor}`}>
                                            {formatSessionTime(session.eventStartDate, session.eventStartTime, session.eventEndTime)}
                                        </div>
                                    </div>
                                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0 ${badge.className}`}>
                                        {badge.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};
